import type { DashboardTasks, PmsAlert } from '@felix/domain';
import type { ApprovalCard } from './forms/types.js';
import type { ReturnedNotice } from './returned/Returned.js';

// Bana düşen işlerin tipleri domain'dedir (sunucu ve arayüz aynı tanım; ADR 0156)
export {
  type DashboardTasks,
  TASK_KINDS,
  TASK_ROW_LIMIT,
  TASK_SECTION_ORDER,
  TASK_SECTIONS,
  type TaskGroup,
  type TaskItem,
  type TaskKind,
  type TaskSection,
} from '@felix/domain';

/**
 * Pano özeti (`packages/db` `dashboardSummary` ile aynı biçim): ofiste `GET /api/office/dashboard`,
 * gemide `GET /api/ship/v1/dashboard`.
 */
export type DashboardAlert =
  | {
      kind: 'certificate';
      status: 'expired' | 'expiring';
      userId: string;
      fullName: string;
      vesselId: string;
      certificate: string;
      date: string;
    }
  | { kind: 'missing'; userId: string; fullName: string; vesselId: string; count: number }
  | {
      kind: 'rest';
      severity: 'violation' | 'warning';
      userId: string;
      fullName: string;
      vesselId: string;
      date: string;
    }
  | {
      /**
       * Kontrat / MLC (ADR 0076 §4): `date` = min(kontrat bitişi, katılış + 12 ay − 1 gün);
       * `basis` tarihi hangisinin belirlediği.
       */
      kind: 'contract';
      status: 'warning' | 'overdue';
      basis: 'contract' | 'mlc';
      userId: string;
      fullName: string;
      vesselId: string;
      date: string;
      joinedOn: string;
    }
  | {
      /** Gemi saati (ADR 0065 §5): yaklaşan ya da kaptan onayı bekleyen değişiklik. */
      kind: 'clock';
      status: 'upcoming' | 'pending';
      changeId: string;
      /** Kaydı giren. */
      userId: string;
      /** Geminin adı (satırın alt bilgisi). */
      fullName: string;
      vesselId: string;
      date: string;
      effectiveAt: string;
      oldOffsetMin: number;
      newOffsetMin: number;
      changeMin: number;
    }
  | {
      /**
       * Tatbikat (ADR 0128 Faz 2 Ek): gecikmiş, 7 gün içinde vadesi gelen, kaptan kararı bekleyen
       * uygulama ya da erteleme. `fullName` geminin adı, `userId` sorumlu zabit (yoksa boş).
       */
      kind: 'drill';
      status: 'overdue' | 'due' | 'pending_review' | 'postponement';
      planId: string;
      userId: string;
      fullName: string;
      vesselId: string;
      /** Next Due (bekleyen kararlarda talebin günü). */
      date: string;
      templateCode: string;
      templateName: string;
    };

/**
 * Gemi panosunun PMS kartı (`packages/db` `pmsDashboard`; plan M7 Task 20; PMS belgesi §21).
 * Ekranı M7c Task 21c'de bağlanır.
 */
export interface PmsDashboardCard {
  summary: {
    total: number;
    today: number;
    overdue: number;
    dueNext7Days: number;
    dueNext30Days: number;
    criticalOverdue: number;
  };
  openWorkOrders: number;
  openDefects: number;
  openBreakdowns: number;
  todaysJobs: {
    jobId: string;
    code: string;
    title: string;
    equipmentId: string;
    dueOn: string | null;
    status: string;
    critical: boolean;
  }[];
  alerts: PmsAlert[];
  /** Zile giden PMS uyarıları: kritik gecikmeler (kaptan ve başmühendis; K22). */
  bellAlerts: PmsAlert[];
}

/** Ofis panosunun PMS satırı (`fleetPmsSummary`; PMS belgesi §22; K23). */
export interface FleetPmsRow {
  vesselId: string;
  vesselName: string;
  compliance: { due: number; overdue: number; percent: number };
  overdue: number;
  criticalOverdue: number;
  openDefects: number;
  openBreakdowns: number;
  dataAsOf: string | null;
  writer: string;
}

/** Gemi panosunda onayımı bekleyen talep (04.11 notları C4; `@felix/db` ile aynı biçim). */
export interface RequisitionApprovalItem {
  requisitionId: string;
  requisitionNo: string;
  department: string;
  requestedBy: string;
  step: number;
  since: string;
  overdue: boolean;
  total: number | null;
  currency: string;
}

export interface RequisitionApprovalCard {
  pending: number;
  overdue: number;
  items: RequisitionApprovalItem[];
}

export interface DashboardSummary {
  vessels: number;
  crew: { onBoard: number };
  certificates: { valid: number; expiring: number; expired: number; missing: number };
  rest: {
    date: string | null;
    compliant: number;
    warning: number;
    violation: number;
    incomplete: number;
  };
  alerts: DashboardAlert[];
  /** Gemide PMS kartı (PMS'i görebilen kişiye). */
  pms?: PmsDashboardCard;
  /** Ofiste filo PMS özeti. */
  pmsFleet?: FleetPmsRow[];
  /**
   * "Pending approvals" kartı (ADR 0042 §16, Ek U29): kişinin karar verebileceği ISM form
   * adımları; yalnız onay ya da iade yetkisi olana. Kişi uyarılarından (`alerts`) ayrıdır.
   */
  approvals?: ApprovalCard;
  /** Gemide kaptan onayı bekleyen malzeme talepleri (04.11 notları C4); yoksa alan yok. */
  requisitionApprovals?: RequisitionApprovalCard;
  /** "Returned to you": kişiye dönen iadeler ve retler, bütün onay akışları (ADR 0116 §1, Ek). */
  returnedToMe?: ReturnedNotice[];
  /** Harici yedek kartı (ADR 0041 §8): gemide `devices.view` izni olana. */
  backup?: BackupDashboardCard;
}

/**
 * Ofis panosu (`GET /api/office/dashboard`; ADR 0067 §5): kişinin görme izni olmayan kart yanıtta
 * yoktur; `certificates` personel belgeleri, `rest` dinlenme izniyle gelir.
 */
export type OfficeDashboardSummary = Omit<DashboardSummary, 'certificates' | 'rest'> &
  Partial<Pick<DashboardSummary, 'certificates' | 'rest'>> & {
    /**
     * Bana düşen işler (ADR 0156): eklemeli alan. Yoksa kişide iş doğuran izin yoktur (ya da
     * sunucu eski); `{ total: 0, groups: [] }` izin var, bekleyen iş yok demektir.
     */
    tasks?: DashboardTasks;
    /** Pano başlığında kişinin rolleri (04.11 notları C3); eski sunucu göndermez. */
    viewer?: { roles: string[] };
  };

/** Ofis panosunun yanıtta gelen kartları; hiçbiri yoksa pano boş durum gösterir (ADR 0067 §5). */
export function officeDashboardCards(summary: OfficeDashboardSummary) {
  const cards = {
    certificates: summary.certificates !== undefined,
    rest: summary.rest !== undefined,
    pms: summary.pmsFleet !== undefined,
    approvals: summary.approvals !== undefined,
    // Yalnız iş izni olan kişi de panoyu görür (ADR 0156; ADR 0067 §5 genişler)
    tasks: summary.tasks !== undefined,
  };
  return { ...cards, any: Object.values(cards).some(Boolean) };
}

/** Harici yedeğin pano kartı (`packages/db` `backupDashboard`; ADR 0041 §8). */
export interface BackupDashboardCard {
  level: 'ok' | 'warning' | 'critical';
  codes: string[];
  targetState: string;
  targetLabel: string | null;
  encrypted: string | null;
  lastSnapshotAt: string | null;
  lastVerifyAt: string | null;
  lastVerifyOk: boolean | null;
  unbackedShipFiles: number;
  companyFilesMissing: number;
  restoreConflicts: number;
  reportedAt: string;
}

/**
 * Zildeki sayı: eylem isteyen uyarılar (eksik belge listesi uzun olabilir; zil onları saymaz).
 * İş listesi geldiyse (web ofis; ADR 0156) form kartı yerine bütün işler sayılır: form adımları
 * da işlerin içindedir. Gelmediyse (masaüstü, eski sunucu) bugünkü hesap sürer.
 */
export function notificationCount(summary: OfficeDashboardSummary | null | undefined): number {
  // Karar bekleyen form adımları da zile sayılır (mimari belge §7.3; ADR 0042 §16)
  return (
    (summary?.alerts ?? []).filter((alert) => alert.kind !== 'missing').length +
    (summary?.tasks ? summary.tasks.total : (summary?.approvals?.pending ?? 0)) +
    // Yedek alarmı tek uyarıdır (ADR 0041 §8: kaptanın zili)
    (summary?.backup && summary.backup.level !== 'ok' ? 1 : 0)
  );
}

/**
 * Uyarının ekranı ("View"; M6f G1-27 … G1-29): belge uyarısı kişinin belgelerini, dinlenme uyarısı
 * kişinin dinlenme saatlerini açar. Kişi `?user=` sorgusuyla seçilir (kabuk `search`; M6f G1-11).
 */
export function alertPath(alert: DashboardAlert): string {
  // Saat değişikliği gemi saati kaydını açar (ADR 0065 §5)
  if (alert.kind === 'clock') return '/crew/ship-clock';
  // Tatbikat planlayıcıda planın satırı (ADR 0128 Faz 2 Ek; ADR 0156 hata düzeltmesi)
  if (alert.kind === 'drill') {
    const params = new URLSearchParams({ plan: alert.planId, vessel: alert.vesselId });
    return `/operations/drills/planner?${params.toString()}`;
  }
  const user = `?user=${encodeURIComponent(alert.userId)}`;
  // Kontrat uyarısı kişinin satırını personel listesinde açar (ADR 0076 §5)
  if (alert.kind === 'contract') return `/crew${user}`;
  return alert.kind === 'rest' ? `/crew/rest-hours${user}` : `/crew/certificates${user}`;
}
